import React, { useCallback, useEffect, useState } from "react";
import {
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TablePagination,
  Box,
  Typography,
  Chip,
  Collapse,
  IconButton,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Grid,
  Button,
  Tooltip,
  Alert,
  LinearProgress,
  Divider,
  Paper,
} from "@mui/material";
import {
  IconRefresh,
  IconDownload,
  IconChevronDown,
  IconChevronUp,
  IconFilter,
  IconFilterOff,
  IconShield,
  IconAlertTriangle,
  IconX,
  IconCheck,
  IconBan,
} from "@tabler/icons-react";
import MainCard from "ui-component/cards/MainCard";
import SearchSection from "layout/MainLayout/Header/SearchSection";
import useContractPermissions from "hooks/useContractPermissions";

// ── Constantes ────────────────────────────────────────────────────────────────

const STATUS_OPTIONS = [
  { value: "", label: "Todos os status" },
  { value: "authorized", label: "Autorizado" },
  { value: "denied", label: "Negado" },
  { value: "failed", label: "Falhou" },
  { value: "cancelled", label: "Cancelado" },
];

const MODULE_OPTIONS = [
  { value: "", label: "Todos os módulos" },
  { value: "contracts", label: "Contratos" },
  { value: "customers", label: "Clientes" },
];

const FORMAT_OPTIONS = [
  { value: "", label: "Todos os formatos" },
  { value: "zip", label: "ZIP" },
  { value: "pdf", label: "PDF" },
  { value: "docx", label: "DOCX" },
  { value: "csv", label: "CSV" },
  { value: "html", label: "HTML" },
];

const STATUS_COLOR = {
  authorized: "success",
  denied: "warning",
  failed: "error",
  cancelled: "default",
};

const STATUS_ICON = {
  authorized: <IconCheck size={14} />,
  denied: <IconAlertTriangle size={14} />,
  failed: <IconX size={14} />,
  cancelled: <IconBan size={14} />,
};

const STATUS_LABEL = {
  authorized: "Autorizado",
  denied: "Negado",
  failed: "Falhou",
  cancelled: "Cancelado",
};

function formatDateTime(ts) {
  if (!ts) return "—";
  return new Date(ts).toLocaleString("pt-BR", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  });
}

// ── Linha expansível ──────────────────────────────────────────────────────────

function LogRow({ log }) {
  const [open, setOpen] = useState(false);

  return (
    <>
      <TableRow
        hover
        sx={{ "& > *": { borderBottom: "unset" }, cursor: "pointer" }}
        onClick={() => setOpen((v) => !v)}
      >
        <TableCell sx={{ width: 36, px: 1 }}>
          <IconButton size="small" tabIndex={-1}>
            {open ? <IconChevronUp size={16} /> : <IconChevronDown size={16} />}
          </IconButton>
        </TableCell>
        <TableCell sx={{ whiteSpace: "nowrap", fontSize: 13 }}>
          {formatDateTime(log.timestamp)}
        </TableCell>
        <TableCell sx={{ fontSize: 13 }}>
          <Typography variant="body2" fontWeight={600} noWrap>
            {log.user_name || "—"}
          </Typography>
          <Typography variant="caption" color="text.secondary" noWrap>
            {log.user_email}
          </Typography>
        </TableCell>
        <TableCell sx={{ fontSize: 13 }}>{log.user_profile || "—"}</TableCell>
        <TableCell>
          <Chip
            size="small"
            color={STATUS_COLOR[log.status] || "default"}
            icon={STATUS_ICON[log.status]}
            label={STATUS_LABEL[log.status] || log.status}
            sx={{ fontWeight: 600, fontSize: 11 }}
          />
        </TableCell>
        <TableCell sx={{ fontSize: 13, maxWidth: 220 }}>
          <Tooltip title={log.data_type}>
            <Typography variant="body2" noWrap>{log.data_type || "—"}</Typography>
          </Tooltip>
        </TableCell>
        <TableCell>
          <Chip
            size="small"
            variant="outlined"
            label={(log.file_format || "—").toUpperCase()}
            sx={{ fontSize: 11, fontWeight: 600 }}
          />
        </TableCell>
        <TableCell sx={{ fontSize: 13 }}>
          {log.customer_name ? (
            <Tooltip title={`ID: ${log.customer_id}`}>
              <span>{log.customer_name}</span>
            </Tooltip>
          ) : "—"}
        </TableCell>
        <TableCell sx={{ fontSize: 13 }}>{log.contract_number || "—"}</TableCell>
        <TableCell align="right" sx={{ fontSize: 13 }}>
          {log.records_count != null ? log.records_count : "—"}
        </TableCell>
        <TableCell sx={{ fontSize: 13 }}>{log.ip_address || "—"}</TableCell>
      </TableRow>

      <TableRow>
        <TableCell colSpan={11} sx={{ py: 0, backgroundColor: "action.hover" }}>
          <Collapse in={open} timeout="auto" unmountOnExit>
            <Box sx={{ px: 3, py: 2 }}>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6} md={3}>
                  <Typography variant="caption" color="text.secondary" display="block">Módulo / Endpoint</Typography>
                  <Typography variant="body2" fontWeight={600}>
                    {log.module}/{log.endpoint}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Typography variant="caption" color="text.secondary" display="block">Perfil / Permissões</Typography>
                  <Typography variant="body2">
                    {log.user_profile || "—"}
                    {log.user_permissions_snapshot && Object.keys(log.user_permissions_snapshot).length > 0 && (
                      <Box component="span" sx={{ ml: 1, color: "text.secondary", fontSize: 11 }}>
                        ({Object.entries(log.user_permissions_snapshot)
                          .filter(([, v]) => v)
                          .map(([k]) => k.replace("can_", "").replace(/_/g, " "))
                          .join(", ") || "nenhuma"})
                      </Box>
                    )}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Typography variant="caption" color="text.secondary" display="block">User Agent / Dispositivo</Typography>
                  <Tooltip title={log.user_agent}>
                    <Typography variant="body2" noWrap sx={{ maxWidth: 300 }}>
                      {log.user_agent || "—"}
                    </Typography>
                  </Tooltip>
                </Grid>
                {log.reason && (
                  <Grid item xs={12} sm={6} md={3}>
                    <Typography variant="caption" color="text.secondary" display="block">Motivo / Finalidade</Typography>
                    <Typography variant="body2">{log.reason}</Typography>
                  </Grid>
                )}
                {log.error_message && (
                  <Grid item xs={12}>
                    <Typography variant="caption" color="error" display="block">Mensagem de Erro</Typography>
                    <Typography variant="body2" color="error.dark" sx={{ fontFamily: "monospace", fontSize: 12 }}>
                      {log.error_message}
                    </Typography>
                  </Grid>
                )}
                {log.extra_data && Object.keys(log.extra_data).length > 0 && (
                  <Grid item xs={12}>
                    <Typography variant="caption" color="text.secondary" display="block">Dados Extras</Typography>
                    <Typography variant="body2" sx={{ fontFamily: "monospace", fontSize: 12, whiteSpace: "pre-wrap", wordBreak: "break-all" }}>
                      {JSON.stringify(log.extra_data, null, 2)}
                    </Typography>
                  </Grid>
                )}
              </Grid>
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
}

// ── Componente principal ──────────────────────────────────────────────────────

const EMPTY_FILTERS = {
  search: "",
  status: "",
  module: "",
  file_format: "",
  date_from: "",
  date_to: "",
};

const DownloadLogList = () => {
  const { isSuperuser, canManageUsers } = useContractPermissions();

  const [logs, setLogs] = useState([]);
  const [totalRecords, setTotalRecords] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [appliedFilters, setAppliedFilters] = useState(EMPTY_FILTERS);
  const [exporting, setExporting] = useState(false);

  const canAccess = isSuperuser || canManageUsers;

  const buildQuery = useCallback((f, pg, rpp) => {
    const params = new URLSearchParams();
    if (f.search) params.set("search", f.search);
    if (f.status) params.set("status", f.status);
    if (f.module) params.set("module", f.module);
    if (f.file_format) params.set("file_format", f.file_format);
    if (f.date_from) params.set("date_from", f.date_from);
    if (f.date_to) params.set("date_to", f.date_to);
    params.set("page", pg + 1);
    params.set("page_size", rpp);
    return params.toString();
  }, []);

  const fetchLogs = useCallback(async (f, pg, rpp) => {
    if (!canAccess) return;
    setLoading(true);
    setError("");
    try {
      const res = await fetch(`/backend/audit/download-logs/?${buildQuery(f, pg, rpp)}`);
      if (!res.ok) throw new Error(`Erro ${res.status}`);
      const data = await res.json();
      setLogs(data.results || data);
      setTotalRecords(data.count ?? (data.results ? data.results.length : data.length));
    } catch (e) {
      setError(e.message || "Erro ao carregar logs.");
    } finally {
      setLoading(false);
    }
  }, [canAccess, buildQuery]);

  useEffect(() => {
    fetchLogs(appliedFilters, page, rowsPerPage);
  }, [fetchLogs, appliedFilters, page, rowsPerPage]);

  const handleApplyFilters = () => {
    setPage(0);
    setAppliedFilters({ ...filters });
  };

  const handleClearFilters = () => {
    setFilters(EMPTY_FILTERS);
    setAppliedFilters(EMPTY_FILTERS);
    setPage(0);
  };

  const handleExportCSV = async () => {
    setExporting(true);
    try {
      const res = await fetch(`/backend/audit/download-logs/export-csv/?${buildQuery(appliedFilters, 0, 999999)}`);
      if (!res.ok) throw new Error("Erro ao exportar.");
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = "logs_download.csv";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (e) {
      alert(e.message);
    } finally {
      setExporting(false);
    }
  };

  const hasActiveFilters = Object.values(appliedFilters).some(Boolean);

  if (!canAccess) {
    return (
      <MainCard title="Log de Downloads">
        <Alert severity="error" icon={<IconShield size={20} />}>
          Acesso restrito. Apenas administradores podem visualizar o log de downloads.
        </Alert>
      </MainCard>
    );
  }

  return (
    <MainCard
      title={
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <IconShield size={20} />
          <span>Log de Downloads / Auditoria</span>
          {hasActiveFilters && (
            <Chip size="small" color="primary" label="Filtros ativos" sx={{ ml: 1, fontSize: 11 }} />
          )}
        </Box>
      }
    >
      {/* ── Barra de ações ── */}
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2, flexWrap: "wrap" }}>
        <SearchSection
          value={filters.search}
          onChange={(e) => setFilters((f) => ({ ...f, search: e.target.value }))}
          onKeyDown={(e) => e.key === "Enter" && handleApplyFilters()}
          placeholder="Buscar por usuário, cliente, contrato, IP..."
        />
        <Tooltip title="Atualizar">
          <IconButton onClick={() => fetchLogs(appliedFilters, page, rowsPerPage)} disabled={loading}>
            <IconRefresh size={20} />
          </IconButton>
        </Tooltip>
        <Tooltip title="Exportar CSV com filtros ativos">
          <span>
            <Button
              variant="outlined"
              size="small"
              startIcon={<IconDownload size={16} />}
              onClick={handleExportCSV}
              disabled={exporting || loading}
            >
              {exporting ? "Exportando..." : "Exportar CSV"}
            </Button>
          </span>
        </Tooltip>
      </Box>

      {/* ── Filtros ── */}
      <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
        <Grid container spacing={2} alignItems="flex-end">
          <Grid item xs={12} sm={6} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel>Status</InputLabel>
              <Select
                value={filters.status}
                label="Status"
                onChange={(e) => setFilters((f) => ({ ...f, status: e.target.value }))}
              >
                {STATUS_OPTIONS.map((o) => (
                  <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel>Módulo</InputLabel>
              <Select
                value={filters.module}
                label="Módulo"
                onChange={(e) => setFilters((f) => ({ ...f, module: e.target.value }))}
              >
                {MODULE_OPTIONS.map((o) => (
                  <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel>Formato</InputLabel>
              <Select
                value={filters.file_format}
                label="Formato"
                onChange={(e) => setFilters((f) => ({ ...f, file_format: e.target.value }))}
              >
                {FORMAT_OPTIONS.map((o) => (
                  <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <TextField
              label="Data inicial"
              type="date"
              size="small"
              fullWidth
              value={filters.date_from}
              onChange={(e) => setFilters((f) => ({ ...f, date_from: e.target.value }))}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <TextField
              label="Data final"
              type="date"
              size="small"
              fullWidth
              value={filters.date_to}
              onChange={(e) => setFilters((f) => ({ ...f, date_to: e.target.value }))}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <Box sx={{ display: "flex", gap: 1 }}>
              <Button
                fullWidth
                variant="contained"
                size="small"
                startIcon={<IconFilter size={16} />}
                onClick={handleApplyFilters}
              >
                Filtrar
              </Button>
              {hasActiveFilters && (
                <Tooltip title="Limpar filtros">
                  <IconButton size="small" onClick={handleClearFilters} color="error">
                    <IconFilterOff size={18} />
                  </IconButton>
                </Tooltip>
              )}
            </Box>
          </Grid>
        </Grid>
      </Paper>

      {/* ── Erros / loading ── */}
      {loading && <LinearProgress sx={{ mb: 1 }} />}
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {/* ── Resumo ── */}
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1 }}>
        <Typography variant="body2" color="text.secondary">
          {totalRecords} {totalRecords === 1 ? "registro" : "registros"} encontrado{totalRecords !== 1 ? "s" : ""}
          {hasActiveFilters && " com os filtros aplicados"}
        </Typography>
      </Box>

      <Divider sx={{ mb: 1 }} />

      {/* ── Tabela ── */}
      <Box sx={{ overflowX: "auto" }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={{ width: 36 }} />
              <TableCell sx={{ fontWeight: 700, whiteSpace: "nowrap" }}>Data / Hora</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Usuário</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Perfil</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Status</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Tipo de Dado</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Formato</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Cliente</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Contrato</TableCell>
              <TableCell align="right" sx={{ fontWeight: 700 }}>Registros</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>IP</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {logs.length === 0 && !loading ? (
              <TableRow>
                <TableCell colSpan={11} align="center" sx={{ py: 4, color: "text.secondary" }}>
                  Nenhum log encontrado.
                </TableCell>
              </TableRow>
            ) : (
              logs.map((log) => <LogRow key={log.id} log={log} />)
            )}
          </TableBody>
        </Table>
      </Box>

      <TablePagination
        component="div"
        count={totalRecords}
        page={page}
        rowsPerPage={rowsPerPage}
        onPageChange={(_, p) => setPage(p)}
        onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
        rowsPerPageOptions={[10, 25, 50, 100]}
        labelRowsPerPage="Linhas por página:"
        labelDisplayedRows={({ from, to, count }) => `${from}–${to} de ${count}`}
      />
    </MainCard>
  );
};

export default DownloadLogList;
